import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Button,
  Collapsible,
  Container,
  Input,
  Textarea,
  VStack,
  HStack,
  Heading,
  Switch,
  Text,
  useDisclosure,
  Spinner,
  Field,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiSave, FiEye } from 'react-icons/fi'
import {
  useScriptDetail,
  useCreateScript,
  useUpdateScript,
  useDefaultResultSettings,
  useTagPicker,
  useQuestionPicker,
} from '../hooks/useScript'
import { scriptApi } from '../lib/api'
import type {
  ScriptLane,
  CanvasResultSettingsFormState,
  SaveScriptRequest,
  AnswerPickerDto,
} from '../types/script'
import { ScriptElementType, ELEMENT_TYPE_LABELS, ContactMethod } from '../types/script'
import {
  OutcomeSettingsEditor,
  ResultSettingsGrid,
  ScriptPreviewDrawer,
  mapElementDtosToFormState,
} from '../components/scriptBuilder'
import { VerticalFlowEditor } from '../components/scriptBuilder/VerticalFlowEditor'
import { flatToLanes, lanesToFlat, createMainLane, getAllElements, getElementSummary } from '../components/scriptBuilder/laneUtils'
import { doorRowOrDefault, replaceRow, DOOR_FOLLOW_UPS } from '../lib/outcomeSettings'
import { useIsMobile } from '../hooks/useIsMobile'
import { DesktopOnlyNotice } from '../components/shared'

export function ScriptBuilderPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = !!id
  const isMobile = useIsMobile()

  const { data: script, isLoading: scriptLoading } = useScriptDetail(id)
  const { data: defaults } = useDefaultResultSettings()
  const { data: tagOptions = [] } = useTagPicker()
  const { data: questionOptions = [] } = useQuestionPicker()
  const createMutation = useCreateScript()
  const updateMutation = useUpdateScript(id || '')
  const { open: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure()

  const [label, setLabel] = useState('')
  const [description, setDescription] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [lanes, setLanes] = useState<ScriptLane[]>([createMainLane()])
  const [resultSettings, setResultSettings] = useState<CanvasResultSettingsFormState[]>([])
  const [questionAnswersCache, setQuestionAnswersCache] = useState<Record<string, AnswerPickerDto[]>>({})
  // Ids we've already issued a fetch for (success OR failure). Prevents refetching
  // the same question every time `lanes` is rebuilt — which would otherwise loop,
  // since rebuilding lanes produces a new array reference on every render.
  const requestedAnswerIdsRef = useRef<Set<string>>(new Set())

  // Load script data in edit mode
  useEffect(() => {
    if (script) {
      const mappedElements = mapElementDtosToFormState(script.elements)
      const reconstructedLanes = flatToLanes(mappedElements, {
        tagOptions,
        questionOptions,
        questionAnswersCache,
      })

      setLabel(script.label)
      setDescription(script.description || '')
      setIsActive(script.isActive)
      setLanes(reconstructedLanes)
      setResultSettings(
        script.resultSettings.map((rs) => ({
          contactMethod: rs.contactMethod,
          allowedResults: rs.allowedResults,
          // A missing allowedFlags means a pre-Phase-6 server ("all follow-ups on") for Door.
          allowedFlags: rs.allowedFlags ?? (rs.contactMethod === ContactMethod.Door ? [...DOOR_FOLLOW_UPS] : []),
        }))
      )
    }
  }, [script, tagOptions, questionOptions, questionAnswersCache])

  // Initialize with defaults for new scripts
  useEffect(() => {
    if (!isEdit && defaults && resultSettings.length === 0) {
      setResultSettings(
        defaults.map((d) => ({
          contactMethod: d.contactMethod,
          allowedResults: d.defaultResults,
          allowedFlags: d.defaultFlags ?? [],
        }))
      )
    }
  }, [isEdit, defaults, resultSettings.length])

  // Populate question answers cache for preview drawer
  useEffect(() => {
    const allElements = getAllElements(lanes)
    const questionIds = allElements
      .filter((el) => el.elementType === ScriptElementType.SurveyQuestion && el.surveyQuestionId)
      .map((el) => el.surveyQuestionId!)

    allElements
      .filter((el) => el.elementType === ScriptElementType.ConditionalBranch && el.conditionalField === 'Answer' && el.conditionalOperator)
      .forEach((el) => {
        if (!questionIds.includes(el.conditionalOperator!)) {
          questionIds.push(el.conditionalOperator!)
        }
      })

    // Fetch each question's answers at most once per mount. Filtering on a
    // "requested" ref (not on the cache) is what breaks the render/fetch loop:
    // a failed or empty-answer fetch never populates the cache, so a cache-based
    // filter would re-fire the same request on every lane rebuild forever.
    const toFetch = questionIds.filter((qid) => !requestedAnswerIdsRef.current.has(qid))
    if (toFetch.length === 0) return

    for (const qid of toFetch) {
      requestedAnswerIdsRef.current.add(qid)
      scriptApi.getQuestionAnswers(qid).then((res) => {
        setQuestionAnswersCache((prev) => ({ ...prev, [qid]: res.data.answers ?? [] }))
      }).catch(() => {
        // Silently ignore - preview will show truncated IDs. Left in the requested
        // set so a transient failure isn't retried in a loop.
      })
    }
  }, [lanes])

  const handleSave = async () => {
    if (!label.trim()) {
      toaster.create({
        title: 'Validation error',
        description: 'Script label is required',
        type: 'error',
        duration: 3000,
      })
      return
    }

    // Flatten lanes to flat element list for backend
    const flatElements = lanesToFlat(lanes)

    const request: SaveScriptRequest = {
      label: label.trim(),
      description: description.trim() || null,
      isActive,
      elements: flatElements.map((el, idx) => ({
        clientId: el.clientId,
        elementType: el.elementType,
        displayOrder: idx,
        parentClientId: el.parentClientId || null,
        nextElementClientId: el.nextElementClientId || null,
        textContent: el.textContent || null,
        textContentSpanish: el.textContentSpanish || null,
        surveyQuestionId: el.surveyQuestionId || null,
        tagId: el.tagId || null,
        conditionalField: el.conditionalField || null,
        conditionalOperator: el.conditionalOperator || null,
        conditionalValue: el.conditionalValue || null,
        nextElementOnTrueClientId: el.nextElementOnTrueClientId || null,
        nextElementOnFalseClientId: el.nextElementOnFalseClientId || null,
        answerBranchesClientIds:
          el.answerBranches && Object.keys(el.answerBranches).length > 0
            ? el.answerBranches
            : null,
        promptText: el.promptText || null,
        promptResponses:
          el.promptResponses && el.promptResponses.length > 0
            ? el.promptResponses.map((pr) => ({
                label: pr.label,
                targetElementClientId: pr.targetClientId || null,
              }))
            : null,
      })),
      resultSettings: replaceRow(resultSettings, doorRowOrDefault(resultSettings, defaults)).map((rs) => ({
        contactMethod: rs.contactMethod,
        allowedResults: rs.allowedResults,
        allowedFlags: rs.allowedFlags,
      })),
    }

    try {
      if (isEdit) {
        await updateMutation.mutateAsync(request)
        toaster.create({
          title: 'Script updated',
          description: 'The script has been updated successfully.',
          type: 'success',
          duration: 3000,
        })
      } else {
        await createMutation.mutateAsync(request)
        toaster.create({
          title: 'Script created',
          description: 'The script has been created successfully.',
          type: 'success',
          duration: 3000,
        })
      }
      navigate('/dashboard/canvassing/scripts')
    } catch (error) {
      toaster.create({
        title: 'Error',
        description: `Failed to ${isEdit ? 'update' : 'create'} script. Please try again.`,
        type: 'error',
        duration: 5000,
      })
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending
  const allElements = getAllElements(lanes)

  if (isEdit && scriptLoading) {
    return (
      <Container maxW="container.xl" py={8}>
        <VStack py={12}>
          <Spinner size="lg" color="accent" />
          <Text color="gray.500">Loading script...</Text>
        </VStack>
      </Container>
    )
  }

  if (isMobile) {
    return (
      <DesktopOnlyNotice
        title="The Script Builder is coming to mobile"
        message="Editing canvassing script flows isn't available on phones yet."
        desktopHint="Open this script on a desktop browser to edit it."
      >
        {allElements.length > 0 && (
          <VStack align="stretch" gap={1}>
            {allElements.map((el) => (
              <Text key={el.clientId} fontSize="sm" color="gray.600" lineClamp={1}>
                {ELEMENT_TYPE_LABELS[el.elementType]}: {getElementSummary(el, { tagOptions, questionOptions })}
              </Text>
            ))}
          </VStack>
        )}
      </DesktopOnlyNotice>
    );
  }

  return (
    <Container maxW="full" px={6} py={8}>
      <VStack gap={6} align="stretch">
        <HStack justify="space-between">
          <Heading size="lg">{isEdit ? 'Edit Script' : 'Create Script'}</Heading>
          <HStack>
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/canvassing/scripts')}>
              Cancel
            </Button>
            {allElements.length > 0 && (
              <Button size="sm" variant="outline" onClick={onPreviewOpen}><FiEye />Preview
                              </Button>
            )}
            <Button colorPalette="brand" size="sm" onClick={handleSave} loading={isSaving}><FiSave />Save
                          </Button>
          </HStack>
        </HStack>

        {/* Script Details */}
        <Box bg="surface" p={6} borderRadius="lg" shadow="md">
          <VStack gap={4} align="stretch">
            <Heading size="md" mb={2}>
              Script Details
            </Heading>

            <Field.Root required>
              <Field.Label fontSize="sm">Label</Field.Label>
              <Input
                size="sm"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="My Canvassing Script"
              />
            </Field.Root>

            <Field.Root>
              <Field.Label fontSize="sm">Description</Field.Label>
              <Textarea
                size="sm"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the purpose of this script..."
              />
            </Field.Root>

            <Field.Root>
              <HStack justify="space-between">
                <Text fontSize="sm" fontWeight="medium">
                  Active
                </Text>
                <Switch.Root checked={isActive} onCheckedChange={(e) => setIsActive(e.checked === true)} >
                  <Switch.HiddenInput />
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                </Switch.Root>
              </HStack>
            </Field.Root>
          </VStack>
        </Box>

        {/* Lane-Based Script Editor */}
        <Box bg="surface" p={6} borderRadius="lg" shadow="md">
          <VStack gap={4} align="stretch">
            <HStack justify="space-between">
              <Box>
                <Heading size="md">Script Flow</Heading>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Build your script in the main flow. Use branch paths to create alternative routes.
                </Text>
              </Box>
            </HStack>

            <VerticalFlowEditor
              lanes={lanes}
              onLanesChange={setLanes}
              tagOptions={tagOptions}
              questionOptions={questionOptions}
            />
          </VStack>
        </Box>

        {/* Door outcomes (spec §5.3). C1's "needs" card (C1 §7.2) mounts directly below this card. */}
        <Box bg="surface" p={6} borderRadius="lg" shadow="md">
          <VStack gap={4} align="stretch">
            <Heading size="md">Door outcomes</Heading>
            <Text fontSize="sm" color="fg.muted">What canvassers can record at the door. Grey items are always on.</Text>
            <OutcomeSettingsEditor
              value={doorRowOrDefault(resultSettings, defaults)}
              onChange={(row) => setResultSettings((prev) => replaceRow(prev, row))}
            />
            <Collapsible.Root>
              <Collapsible.Trigger asChild>
                <Button variant="ghost" minH="44px" alignSelf="flex-start">Other contact methods</Button>
              </Collapsible.Trigger>
              <Collapsible.Content>
                <Text fontSize="sm" color="fg.muted" mb={2}>Phone, event, text and email results. Not used by the canvasser app yet.</Text>
                <Box overflowX="auto">
                  <ResultSettingsGrid resultSettings={resultSettings} onChange={setResultSettings} />
                </Box>
              </Collapsible.Content>
            </Collapsible.Root>
          </VStack>
        </Box>
      </VStack>

      <ScriptPreviewDrawer
        isOpen={isPreviewOpen}
        onClose={onPreviewClose}
        elements={allElements}
        tagOptions={tagOptions}
        questionOptions={questionOptions}
        questionAnswersCache={questionAnswersCache}
      />
    </Container>
  );
}
